/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, OnDestroy, OnInit, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { BuilderNavigationService, FilterNames, FilterRestService } from '@gh/builder-shared';
import {
  AelEvents,
  BuilderPermissionSet,
  getAelEventsArray,
  ScheduleType,
  ScheduleDetailsDto,
} from '@gh/builder-shared-data';
import {
  cellValueFormatter,
  rendererIconByName,
  truthyCellRenderer,
} from '@gh/common-internal-shared';
import {
  AppRouter,
  DateTimeTypes,
  EnumDictionary,
  filterableForm,
  FilterableFormState,
  FormStateBuilder,
  getAllNewSelectedEntities,
  getRouteInitData,
  LocalizationBusinessService,
  UserContext,
} from '@gh/common-shared';
import { filterTracker, RouteDataService, SlicedDataSource } from '@gh/core-data';
import {
  DialogService,
  NotificationService,
  WaitingService,
  yesAnswer,
  yesNoConfirmation,
} from '@gh/core-ui';
import {
  buildDateTimeColumn,
  cursorTracker,
  GridDefinitionFactory,
  LayoutBasedGridDefinition,
  newGridPaginationCursor,
} from '@gh/core-ui-grid';
import { filter$, map$, switchMap$, debounceTime$, skip$, takeUntil$ } from '@gh/rx/operators';
import { pick } from 'lodash';
import { Subject } from 'rxjs';
import { ScheduleFinderDialogConfig } from '../../resources/schedule-finder-business.service';
import { ScheduleFinderListItem } from '../../resources/schedule-finder.model';
import { ScheduleListBusinessService } from '../../resources/schedule-list-business.service';
import { SalesScheduleFilter, SalesScheduleListItem } from '../../resources/schedule-list.model';
import { salesScheduleCreateDialogRoute } from '../sales-schedule-create-dialog/sales-schedule-create-dialog.component';
import { scheduleFinderDialogRoute } from '../schedule-finder-dialog/schedule-finder-dialog.route';
import { ID } from 'common-shared-data/common';

const FILTER_PERSIST_DEBOUNCE_MS = 300;

@Component({
  standalone: false,
  selector: 'gh-sales-schedule-list',
  templateUrl: 'sales-schedule-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SalesScheduleListComponent implements OnInit, OnDestroy {
  gridDefinition: LayoutBasedGridDefinition;
  cursor = newGridPaginationCursor();
  dataSource: SlicedDataSource<SalesScheduleListItem>;
  form: FilterableFormState<SalesScheduleFilter>;
  filter: any;

  private readonly destroy$ = new Subject<void>();
  private scheduleTypeDict: EnumDictionary<ScheduleType>;

  constructor(private route: ActivatedRoute,
              private router: AppRouter,
              private userContext: UserContext,
              private routeData: RouteDataService,
              private formStateBuilder: FormStateBuilder,
              private dialogs: DialogService,
              private notifications: NotificationService,
              private waitings: WaitingService,
              private filterService: FilterRestService,
              private navigations: BuilderNavigationService,
              private gridDefinitionFactory: GridDefinitionFactory,
              private localizationBusinessService: LocalizationBusinessService,
              private business: ScheduleListBusinessService,
              private changeDetectorRef: ChangeDetectorRef) {
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnInit(): void {
    const { filter: initFilter, gridLayout, scheduleTypeDict } = getRouteInitData(this.route);
    this.scheduleTypeDict = scheduleTypeDict;
    this.gridDefinition = this.gridDefinitionFactory.buildLayoutBased(
      {
        startDate: buildDateTimeColumn(DateTimeTypes.ShortDate),
        endDate: buildDateTimeColumn(DateTimeTypes.ShortDate),
        isTodoExist: { cellRenderer: truthyCellRenderer(rendererIconByName('done')) },
        duration: {
          valueFormatter: cellValueFormatter<number>((duration) =>
            this.localizationBusinessService.formatHoursDuration(duration)),
        },
      },
      {
        getRowId: (params) => String(params.data.scheduleId)
      });
    this.gridDefinition.bind(gridLayout);

    this.form = filterableForm(this.formStateBuilder.buildPlain({
      value: initFilter,
      children: {
        searchString: void 0,
        showWillNotCompleted: void 0,
        showCompleted: void 0,
        showActive: void 0,
      },
    }));

    const tracker = this.routeData
      .tracker(filterTracker(this.form))
      .withEffects(cursorTracker(this.cursor));
    tracker.listen(this.route);
    tracker.filterChanges.pipe(
      takeUntil$(this.destroy$),
    ).subscribe(([filter]) => {
      this.dataSource = this.business.findAllSalesSchedulesAsDataSource(filter);
      this.changeDetectorRef.markForCheck();
    });
    tracker.filterChanges.pipe(
      debounceTime$(FILTER_PERSIST_DEBOUNCE_MS),
      skip$(1),
      switchMap$(([filter]) => this.filterService.update(FilterNames.salesSchedule, filter)),
      takeUntil$(this.destroy$),
    ).subscribe({ error: this.notifications.processAll });
  }

  onCreate(): void {
    this.dialogs.openWithInput(salesScheduleCreateDialogRoute, {}).afterClosed().pipe(
      filter$(Boolean))
      .subscribe((init: { salesFolderId: ID; templateId: ID }) => {
        this.business.init(init.salesFolderId, init.templateId, ScheduleType.Sales)
          .subscribe(
            (scheduleDetails: ScheduleDetailsDto) => {
              if (scheduleDetails.errorMessage && scheduleDetails.errorMessage.trim() !== "") {
                this.notifications.warning("msg_cyclic_depedency", { issues: scheduleDetails.errorMessage });
              } else {
                this.router.navigateToChild(['schedules/sales/new/gantt-chart'], {
                  queryParams: pick(init, 'salesFolderId', 'templateId'),
                });
              }
            },
            this.notifications.processAll,
          );
      });
  }

  onSearch(): void {
    this.form.emitFilter();
  }

  goToAel(schedule?: SalesScheduleListItem): void {
    this.navigations.goToAel({
      label: 'lbl_sales_schedules',
      entityType: 'SALES_FOLDER',
      entityId: schedule ? schedule.salesFolderId : void 0,
      eventTypeIds: getAelEventsArray(AelEvents.SalesFolders.SalesSchedule),
    });
  }

  onOpenCombinedGanttChart(): void {
    this.dialogs.openWithInput(scheduleFinderDialogRoute, <ScheduleFinderDialogConfig>{
      type: ScheduleType.Sales,
      multiselection: true,
    }).afterClosed().pipe(
      filter$(Boolean),
      map$(getAllNewSelectedEntities))
      .subscribe((items: ScheduleFinderListItem[]) => {
        this.router.navigateToChild(['schedules/sales/combined/gantt-chart'], {
          queryParams: { scheduleIds: items.map((item) => item.scheduleId).join() },
        });
      });
  }

  onOpen(schedule: SalesScheduleListItem): void {
    if (this.userContext.hasPermission(BuilderPermissionSet.Schedules.Sales.EDIT_VIEW)) {
      this.router.navigateToChild(['schedules/sales', schedule.scheduleId, 'gantt-chart']);
    }
  }

  onRemove(schedule: SalesScheduleListItem): void {
    this.dialogs.confirmation(yesNoConfirmation('msg_sch_sales_delete', {
      schType: this.scheduleTypeDict.get(ScheduleType.Sales),
      sfName: schedule.name,
    })).pipe(
      filter$(yesAnswer),
      switchMap$(() => this.business.removeSalesSchedule(schedule).pipe(this.waitings.operator())))
      .subscribe(
        () => this.loadData(),
        this.notifications.processAll);
  }

  private loadData(): void {
    this.dataSource = this.business.findAllSalesSchedulesAsDataSource(this.form.submitValue);
  }
}
